// src/pages/HabitsConfigPage.tsx
// Enhancements:
// 1. Inline edit form per habit (name, objective, description, frequency, penalty)
// 2. Type change blocked — shows suggestion to deactivate and create new
// 3. Inactive habits collapsed by default

import React, { useState, useEffect } from 'react';
import { habitsApi } from '../api';
import type { Habit, HabitGroups, CreateHabitInput, HabitFrequency, HabitType } from '../types';

const FREQUENCIES: { value: HabitFrequency; label: string }[] = [
  { value:'daily',        label:'Daily' },
  { value:'5x_week',      label:'5× per week' },
  { value:'4x_week',      label:'4× per week' },
  { value:'3x_week',      label:'3× per week' },
  { value:'2x_week',      label:'2× per week' },
  { value:'weekly',       label:'Once per week' },
  { value:'weekly_limit', label:'Weekly limit' },
];

const FREQ_HINT: Record<HabitFrequency, string> = {
  daily:        'Log every day',
  '5x_week':    'Tracked weekly — 5 times per week',
  '4x_week':    'Tracked weekly — 4 times per week',
  '3x_week':    'Tracked weekly — 3 times per week',
  '2x_week':    'Tracked weekly — 2 times per week',
  weekly:       'Tracked weekly — once per week',
  weekly_limit: 'Tracked weekly — log each occurrence against the limit',
};

const STATUS_COLOR: Record<string, string> = {
  priority: 'var(--green)', active: 'var(--blue)',
  graduated: 'var(--purple)', waiting: 'var(--text3)', inactive: 'var(--text3)',
};

// ── EDIT FORM ─────────────────────────────────────────────────────

interface EditFormProps {
  habit: Habit;
  onSave: () => void;
  onCancel: () => void;
}

function EditForm({ habit, onSave, onCancel }: EditFormProps) {
  const [name, setName]           = useState(habit.name);
  const [objective, setObjective] = useState(habit.objective);
  const [description, setDesc]    = useState(habit.description);
  const [frequency, setFrequency] = useState<HabitFrequency>(habit.frequency);
  const [penalty, setPenalty]     = useState(habit.penaltyAmount);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await habitsApi.update(habit.habitId, {
        name, objective, description, frequency,
        ...(habit.type === 'break' ? { penaltyAmount: penalty } : {}),
      });
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  const freqChanged = frequency !== habit.frequency;

  return (
    <form onSubmit={save} style={{
      marginTop: 12, padding: 16,
      background: 'var(--bg3)', borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border2)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 14 }}>
        Edit habit
        <span style={{
          marginLeft: 8, fontSize: 9, fontFamily: 'var(--font-mono)',
          padding: '2px 6px', borderRadius: 3,
          background: 'rgba(255,181,71,0.1)', color: 'var(--orange)',
          border: '1px solid rgba(255,181,71,0.2)',
        }}>
          {habit.type} — cannot be changed
        </span>
      </div>

      {/* Type change notice */}
      <div style={{
        padding: '10px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 14,
        background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)',
        fontSize: 11, color: 'var(--text2)', lineHeight: 1.5,
      }}>
        💡 Want to change the type (build ↔ break)? Deactivate this habit and create a new one.
        Your history stays intact.
      </div>

      {error && (
        <div style={{ color: 'var(--red)', fontSize: 11, marginBottom: 12 }}>{error}</div>
      )}

      <FormField label="Habit name" required>
        <input value={name} onChange={e => setName(e.target.value)} required style={inp} />
      </FormField>

      <FormField label="Objective — why this matters" required>
        <input value={objective} onChange={e => setObjective(e.target.value)}
          placeholder="e.g. Feel more rested" required style={inp} />
      </FormField>

      <FormField label="Description — what counts">
        <input value={description} onChange={e => setDesc(e.target.value)}
          placeholder="e.g. In bed by 12am, 7-8 hours" style={inp} />
      </FormField>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <div>
          <FormField label="Frequency">
            <select value={frequency} onChange={e => setFrequency(e.target.value as HabitFrequency)} style={inp}>
              {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </FormField>
          {/* Frequency hint */}
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, fontStyle: 'italic' }}>
            {FREQ_HINT[frequency]}
          </div>
          {freqChanged && (
            <div style={{
              fontSize: 10, color: 'var(--orange)', marginTop: 4,
              padding: '4px 8px', borderRadius: 4,
              background: 'var(--orange-dim)', border: '1px solid rgba(255,181,71,0.2)',
            }}>
              ⚠ Frequency change applies from next week onwards
            </div>
          )}
        </div>

        {habit.type === 'break' && (
          <FormField label="Penalty per slip (IDR)">
            <input type="number" value={penalty}
              onChange={e => setPenalty(Number(e.target.value))} style={inp} />
          </FormField>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button type="submit" disabled={saving} style={{
          padding: '9px 20px', borderRadius: 'var(--radius-sm)', fontSize: 12,
          background: 'var(--green)', border: 'none', color: '#0c0e14',
          fontWeight: 600, cursor: 'pointer',
        }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel} style={{
          padding: '9px 16px', borderRadius: 'var(--radius-sm)', fontSize: 12,
          background: 'transparent', border: '1px solid var(--border)',
          color: 'var(--text2)', cursor: 'pointer',
        }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── HABIT CARD ────────────────────────────────────────────────────

interface HabitCardProps {
  habit: Habit;
  onToggle: (h: Habit) => void;
  onEdited: () => void;
}

function HabitCard({ habit: h, onToggle, onEdited }: HabitCardProps) {
  const [editing, setEditing] = useState(false);

  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{h.name}</span>
            <span style={{
              fontSize: 8, fontFamily: 'var(--font-mono)', padding: '2px 6px',
              borderRadius: 3, background: 'var(--bg3)',
              color: STATUS_COLOR[h.status] || 'var(--text3)',
              border: '1px solid var(--border)',
            }}>
              {h.status}
            </span>
            {h.isPriority  && <span style={{ fontSize: 9 }}>⭐</span>}
            {h.isIntention && <span style={{ fontSize: 9 }}>🎯</span>}
          </div>

          <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>
            {h.type === 'break' ? '🚫 break' : '🔨 build'} · {FREQ_HINT[h.frequency]}
            {h.penaltyAmount > 0 && ` · IDR ${h.penaltyAmount.toLocaleString()}/slip`}
          </div>

          {h.objective && (
            <div style={{ fontSize: 10, color: 'var(--text3)', fontStyle: 'italic' }}>
              {h.objective}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {/* Edit button — only for non-inactive */}
          {h.status !== 'inactive' && (
            <button onClick={() => setEditing(!editing)} style={{
              padding: '5px 12px', borderRadius: 'var(--radius-sm)', fontSize: 11,
              background: editing ? 'rgba(167,139,250,0.1)' : 'var(--bg3)',
              border: editing ? '1px solid rgba(167,139,250,0.3)' : '1px solid var(--border)',
              color: editing ? 'var(--purple)' : 'var(--text3)', cursor: 'pointer',
            }}>
              {editing ? 'Cancel' : 'Edit'}
            </button>
          )}

          {/* Activate / Deactivate */}
          <button onClick={() => onToggle(h)} style={{
            padding: '5px 12px', borderRadius: 'var(--radius-sm)', fontSize: 11,
            background: ['active','priority'].includes(h.status) ? 'var(--red-dim)' : 'var(--green-dim)',
            border: ['active','priority'].includes(h.status)
              ? '1px solid rgba(255,94,125,0.2)'
              : '1px solid rgba(0,229,160,0.2)',
            color: ['active','priority'].includes(h.status) ? 'var(--red)' : 'var(--green)',
            cursor: 'pointer',
          }}>
            {['active','priority'].includes(h.status) ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Inline edit form */}
      {editing && (
        <EditForm
          habit={h}
          onSave={() => { setEditing(false); onEdited(); }}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

// ── COLLAPSIBLE SECTION ───────────────────────────────────────────

function CollapsibleSection({ label, count, defaultOpen = true, children }: {
  label: string; count: number; defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 20 }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        background: 'transparent', border: 'none', cursor: 'pointer',
        padding: '4px 0', marginBottom: open ? 10 : 0,
      }}>
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase',
          color: 'var(--text3)', fontFamily: 'var(--font-mono)',
        }}>
          {label}
        </span>
        <span style={{
          fontSize: 9, fontFamily: 'var(--font-mono)', padding: '1px 6px',
          borderRadius: 10, background: 'var(--bg3)', color: 'var(--text3)',
          border: '1px solid var(--border)',
        }}>
          {count}
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 12 }}>
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────

export default function HabitsConfigPage() {
  const [habits, setHabits]     = useState<HabitGroups | null>(null);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState<Partial<CreateHabitInput>>({ type: 'build', frequency: 'daily' });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const load = async () => {
    setLoading(true);
    try { const { habits: h } = await habitsApi.list(); setHabits(h); } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await habitsApi.create(form as CreateHabitInput);
      setShowForm(false);
      setForm({ type: 'build', frequency: 'daily' });
      await load();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  const toggle = async (habit: Habit) => {
    if (['active', 'priority'].includes(habit.status)) {
      await habitsApi.deactivate(habit.habitId);
    } else {
      await habitsApi.activate(habit.habitId);
    }
    await load();
  };

  const activeHabits   = habits ? [...habits.priority, ...habits.active, ...habits.waiting, ...habits.graduated] : [];
  const inactiveHabits = habits?.inactive || [];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: -0.5 }}>Habits</div>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: '8px 18px', borderRadius: 'var(--radius-sm)', fontSize: 12,
          background: 'var(--green)', border: 'none', color: '#0c0e14',
          fontWeight: 600, cursor: 'pointer',
        }}>
          + New habit
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 14 }}>New habit</div>
          {error && <div style={{ color: 'var(--red)', fontSize: 11, marginBottom: 10 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
            <FormField label="Habit name" required>
              <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Sleep 7-8h" required style={inp} />
            </FormField>
            <FormField label="Type">
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as HabitType })} style={inp}>
                <option value="build">Build (start doing)</option>
                <option value="break">Break (stop doing)</option>
              </select>
            </FormField>
          </div>

          <FormField label="Objective — why this matters" required>
            <input value={form.objective || ''} onChange={e => setForm({ ...form, objective: e.target.value })}
              placeholder="e.g. Feel more rested and energetic" required style={inp} />
          </FormField>

          <FormField label="Description — what counts">
            <input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. In bed by 12am, 7-8 hours total" style={inp} />
          </FormField>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 12 }}>
            <div>
              <FormField label="Frequency">
                <select value={form.frequency}
                  onChange={e => setForm({ ...form, frequency: e.target.value as HabitFrequency })} style={inp}>
                  {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </FormField>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, fontStyle: 'italic' }}>
                {FREQ_HINT[form.frequency || 'daily']}
              </div>
            </div>
            {form.type === 'break' && (
              <FormField label="Penalty per slip (IDR)">
                <input type="number" value={form.penaltyAmount || ''}
                  onChange={e => setForm({ ...form, penaltyAmount: Number(e.target.value) })}
                  placeholder="e.g. 5000" style={inp} />
              </FormField>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button type="submit" disabled={saving} style={{
              padding: '9px 20px', borderRadius: 'var(--radius-sm)', fontSize: 12,
              background: 'var(--green)', border: 'none', color: '#0c0e14',
              fontWeight: 600, cursor: 'pointer',
            }}>
              {saving ? 'Saving...' : 'Create habit'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} style={{
              padding: '9px 16px', borderRadius: 'var(--radius-sm)', fontSize: 12,
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text2)', cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={{ color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>loading habits...</div>
      ) : (
        <>
          {/* Active / Priority / Waiting / Graduated */}
          {activeHabits.length > 0 && (
            <CollapsibleSection label="Active" count={activeHabits.length} defaultOpen>
              {activeHabits.map(h => (
                <HabitCard key={h.habitId} habit={h} onToggle={toggle} onEdited={load} />
              ))}
            </CollapsibleSection>
          )}

          {/* Inactive — collapsed by default */}
          {inactiveHabits.length > 0 && (
            <CollapsibleSection label="Inactive" count={inactiveHabits.length} defaultOpen={false}>
              {inactiveHabits.map(h => (
                <HabitCard key={h.habitId} habit={h} onToggle={toggle} onEdited={load} />
              ))}
            </CollapsibleSection>
          )}

          {activeHabits.length === 0 && inactiveHabits.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 14, color: 'var(--text2)' }}>No habits yet. Create your first one above.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FormField({ label, required, children }: {
  label: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{
        display: 'block', fontSize: 10, color: 'var(--text3)', marginBottom: 4,
        fontFamily: 'var(--font-mono)', letterSpacing: '0.3px',
      }}>
        {label}{required ? ' *' : ''}
      </label>
      {children}
    </div>
  );
}

const inp: React.CSSProperties = {
  width: '100%', padding: '8px 10px', background: 'var(--bg3)',
  border: '1px solid var(--border2)', borderRadius: 'var(--radius-sm)',
  color: 'var(--text)', fontSize: 12, outline: 'none',
};
